import {DateValue, parseAbsoluteToLocal} from '@internationalized/date';
import {Fragment, memo} from 'react';
import {useDateFormatter} from '@ui/i18n/use-date-formatter';
import {shallowEqual} from '@ui/utils/shallow-equal';
import {useSettings} from '@ui/settings/use-settings';
import {useUserTimezone} from '@ui/i18n/use-user-timezone';

export const DateFormatPresets: Record<
  'numeric' | 'short' | 'long' | 'timestamp' | 'time',
  Intl.DateTimeFormatOptions
> = {
  numeric: {year: 'numeric', month: '2-digit', day: '2-digit'},
  short: {year: 'numeric', month: 'short', day: '2-digit'},
  long: {month: 'long', day: '2-digit', year: 'numeric'},
  timestamp: {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  },
  time: {
    hour: '2-digit',
    minute: '2-digit',
  },
};

interface FormattedDateProps {
  date?: string | DateValue | Date;
  options?: Intl.DateTimeFormatOptions;
  preset?: keyof typeof DateFormatPresets;
}

function toJsDate(
  value: string | DateValue | Date,
  timezone: string,
): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string') {
    const raw = value.trim();
    if (!raw) return null;

    // Preferred: full ISO absolute format with timezone.
    try {
      return parseAbsoluteToLocal(raw).toDate();
    } catch (e) {
      // fallback below
    }

    // Fallback: SQL datetime string without timezone.
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(raw)) {
      const normalized = raw
        .replace(' ', 'T')
        // JS Date supports milliseconds; trim microseconds if present.
        .replace(/(\.\d{3})\d+$/, '$1');
      // Prefer local parse first to avoid fixed +3h offset on servers
      // that already serialize naive datetimes in local/app timezone.
      const localDate = new Date(normalized);
      if (!Number.isNaN(localDate.getTime())) {
        return localDate;
      }
      // Final fallback: treat as UTC if local parse fails.
      const utcDate = new Date(`${normalized}Z`);
      if (!Number.isNaN(utcDate.getTime())) {
        return utcDate;
      }
      return null;
    }

    const nativeDate = new Date(raw);
    return Number.isNaN(nativeDate.getTime()) ? null : nativeDate;
  }

  try {
    const date = value.toDate(timezone);
    return Number.isNaN(date.getTime()) ? null : date;
  } catch (e) {
    return null;
  }
}

export const FormattedDate = memo(
  ({date, options, preset}: FormattedDateProps) => {
    const {dates} = useSettings();
    const timezone = useUserTimezone();
    const formatter = useDateFormatter(
      options ||
        (DateFormatPresets as Record<string, Intl.DateTimeFormatOptions>)[
          preset || dates?.format
        ],
    );

    if (!date) {
      return null;
    }

    const normalizedDate = toJsDate(date, timezone);
    if (!normalizedDate) {
      return null;
    }

    return <Fragment>{formatter.format(normalizedDate)}</Fragment>;
  },
  shallowEqual,
);
